import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Money } from '@principle-theorem/accounting';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import {
  DEFAULT_STATUS_INDICATOR_CONFIG,
  DepositPaymentMode,
  DepositStatus,
  getStatusIndicatorTypeFromDepositStatus,
  StatusIndicatorMode,
  type IDepositMetadata,
  type IStatusIndicatorConfig,
} from '@principle-theorem/principle-core/interfaces';
import { filterUndefined, TypeGuard } from '@principle-theorem/shared';
import { combineLatest, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CurrentBrandScope } from '../../app-state/current-brand';
import { CurrentScopeFacade } from '../../store/current-scope.facade';

const isDepositStatus = TypeGuard.enumValue(DepositStatus);

const DEPOSIT_STATUS_LABELS: Record<DepositStatus, string> = {
  [DepositStatus.Required]: 'Required',
  [DepositStatus.Pending]: 'Pending',
  [DepositStatus.Authorised]: 'Authorised',
  [DepositStatus.Paid]: 'Paid',
  [DepositStatus.Forfeited]: 'Forfeited',
  [DepositStatus.Refunded]: 'Refunded',
  [DepositStatus.Waived]: 'Waived',
  [DepositStatus.Cancelled]: 'Cancelled',
  [DepositStatus.Released]: 'Hold Released',
};

const DEPOSIT_PAYMENT_MODE_LABELS: Record<DepositPaymentMode, string> = {
  [DepositPaymentMode.Charge]: 'Charge',
  [DepositPaymentMode.PreAuth]: 'Pre-Authorisation',
};

@Component({
  selector: 'pr-timeline-deposit-notification',
  templateUrl: './timeline-deposit-notification.component.html',
  styleUrls: ['./timeline-deposit-notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgCommonModule, MatIconModule, MatTooltipModule],
})
export class TimelineDepositNotificationComponent {
  private readonly _currentScope = inject(CurrentScopeFacade);
  private readonly _brandScope = inject(CurrentBrandScope);

  readonly deposit = input.required<IDepositMetadata>();
  readonly tooltipsEnabled = input(false);
  readonly badgeSize = input(14);
  readonly statusIndicatorMode = StatusIndicatorMode;
  readonly white = '#FFFFFF';
  readonly label = computed(() => {
    const deposit = this.deposit();
    const amount = Money.formatPlainCents(
      deposit.amountCents,
      deposit.currency
    );
    const details = [
      `Deposit: ${DEPOSIT_STATUS_LABELS[deposit.status]}`,
      amount,
      DEPOSIT_PAYMENT_MODE_LABELS[deposit.paymentMode],
      deposit.description,
    ];
    return details.filter((detail) => !!detail).join(' · ');
  });

  readonly badgeStyles = computed(() => ({
    width: `${this.badgeSize()}px`,
    height: `${this.badgeSize()}px`,
  }));
  readonly iconStyles = computed(() => ({
    width: `${this.badgeSize()}px`,
    height: `${this.badgeSize()}px`,
    'font-size': `${this.badgeSize()}px`,
  }));

  readonly config$: Observable<IStatusIndicatorConfig | undefined>;
  readonly show$: Observable<boolean>;

  constructor() {
    const deposit$ = toObservable(this.deposit);
    const overrides$ = this._currentScope.currentPractice$.pipe(
      filterUndefined(),
      map((practice) => practice.settings.statusIndicatorOverrides)
    );

    this.config$ = combineLatest([deposit$, overrides$]).pipe(
      map(([deposit, overrides]) => {
        if (!isDepositStatus(deposit.status)) {
          return;
        }
        const indicatorType = getStatusIndicatorTypeFromDepositStatus(
          deposit.status
        );
        if (!indicatorType) {
          return;
        }
        return {
          ...DEFAULT_STATUS_INDICATOR_CONFIG[indicatorType],
          ...overrides?.[indicatorType],
        };
      })
    );
    this.show$ = combineLatest([
      this.config$,
      this._brandScope.depositsEnabled$,
    ]).pipe(
      map(
        ([config, depositsEnabled]) =>
          depositsEnabled && config?.enabled === true
      )
    );
  }
}
